<template>
    <!-- Pricing Section -->
    <div class="py-10 md:py-16 px-4 sm:px-6 lg:px-8">
        <div class="max-w-5xl mx-auto px-0 md:px-6">
            <h2
                class="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 text-center font-medium"
            >
                One plan.
                <span class="text-orange">Built to pay for itself.</span>
            </h2>

            <!-- Billing Toggle -->
            <div class="flex items-center justify-center mt-2">
                <div class="scale-75 origin-right">
                    <Switch
                        :value="isYearlyBilling"
                        @input="$emit('update:isYearlyBilling', $event)"
                        label="Toggle billing cycle"
                    />
                </div>
                <span class="text-xs text-slate-800 dark:text-slate-100 mr-1.5 ml-3">
                    Pay yearly
                </span>
                <span
                    class="text-xs px-2 rounded-lg transition-opacity"
                    :class="
                        isYearlyBilling
                            ? 'bg-[#FEF0E7] text-orange border-orange-200 border'
                            : 'opacity-0'
                    "
                >
                    2 Months Free
                </span>
            </div>

            <!-- Single Pricing Card -->
            <div class="flex flex-col md:flex-row mt-8 w-full">
                <!-- Left: Plan Info -->
                <div
                    class="w-full md:w-1/3 justify-between flex flex-col bg-white dark:bg-slate-800 p-8 rounded-l-lg relative overflow-hidden"
                >
                    <div>
                        <h3 class="text-orange text-3xl font-medium">
                            10xTravel+
                        </h3>
                        <p class="text-slate-800 dark:text-slate-100 text-3xl font-medium">
                            membership
                        </p>
                    </div>

                    <div class="mt-6">
                        <div class="flex items-center gap-2">
                            <div
                                class="flex items-center text-3xl font-normal"
                            >
                                <span
                                    class="text-slate-400 line-through transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap"
                                    :class="
                                        isYearlyBilling
                                            ? 'max-w-[3rem] opacity-100 mr-1'
                                            : 'max-w-0 opacity-0 mr-0'
                                    "
                                >
                                    $10
                                </span>
                                <span
                                    class="text-slate-800 dark:text-slate-100 relative inline-block"
                                >
                                    <span class="invisible">$8.33</span>
                                    <span
                                        class="absolute inset-0 transition-opacity duration-300 ease-in-out"
                                        :class="
                                            isYearlyBilling
                                                ? 'opacity-0'
                                                : 'opacity-100'
                                        "
                                    >
                                        $10
                                    </span>
                                    <span
                                        class="absolute inset-0 transition-opacity duration-300 ease-in-out"
                                        :class="
                                            isYearlyBilling
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        "
                                    >
                                        $8.33
                                    </span>
                                </span>
                            </div>
                            <div class="flex flex-col items-start">
                                <span class="text-slate-800 dark:text-slate-100 text-xs">
                                    per month
                                </span>
                                <span class="text-slate-500 dark:text-slate-400 text-xs">
                                    billed
                                    {{
                                        isYearlyBilling
                                            ? 'yearly'
                                            : 'monthly'
                                    }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="hidden md:flex self-stretch flex-col">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="16"
                        viewBox="0 0 32 16"
                        fill="none"
                        class="block shrink-0"
                        preserveAspectRatio="none"
                    >
                        <path
                            d="M32 16H0V0H3C3 7.1797 8.8203 13 16 13C23.1797 13 29 7.1797 29 0H32V16Z"
                            fill="white"
                        />
                    </svg>
                    <div
                        class="bg-white dark:bg-slate-800 w-8 flex-1 flex flex-col items-center justify-around gap-3 py-2"
                    >
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                        <div
                            class="w-1 flex-1 bg-gradient-to-b from-slate-100 to-slate-50 rounded-full"
                        ></div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="16"
                        viewBox="0 0 32 16"
                        fill="none"
                        class="block shrink-0 rotate-180"
                        preserveAspectRatio="none"
                    >
                        <path
                            d="M32 16H0V0H3C3 7.1797 8.8203 13 16 13C23.1797 13 29 7.1797 29 0H32V16Z"
                            fill="white"
                        />
                    </svg>
                </div>
                <!-- Right: Features List -->
                <div
                    class="flex-1 bg-white dark:bg-slate-800 p-8 rounded-r-lg relative overflow-hidden"
                >
                    <img
                        src="../../../../images/plus-right-plane-gray.svg"
                        alt=""
                        class="absolute -right-32 top-16 w-[400px] opacity-30 pointer-events-none rotate-45"
                    />
                    <p
                        class="text-slate-800 dark:text-slate-100 font-medium mb-4 relative z-10"
                    >
                        What you'll get
                    </p>
                    <ul class="flex flex-col gap-3 list-none">
                        <li class="flex items-center gap-2.5">
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                Members-only points and miles community
                            </span>
                        </li>
                        <li class="flex items-center gap-2.5">
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                High-value award alerts
                            </span>
                        </li>
                        <li class="flex items-center gap-2.5">
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                Credit card benefit tracking across all the
                                cards in your wallet
                            </span>
                        </li>
                        <!-- <li class="flex items-center gap-2.5">
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                Automatic syncing for all your loyalty
                                accounts
                            </span>
                        </li> -->
                        <li class="flex items-center gap-2.5">
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                Personalized recommendations for the best
                                card to use at checkout
                            </span>
                        </li>
                        <li class="flex items-center gap-2.5">
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                Seamless redemption tracking with email
                                import
                            </span>
                        </li>
                        <li
                            v-if="transferBonusAlertsEnabled"
                            class="flex items-center gap-2.5"
                        >
                            <div
                                class="w-4 h-4 bg-emerald-500 rounded-full"
                            >
                                <Icon
                                    icon="check"
                                    :width="16"
                                    :height="16"
                                    class="text-white flex-shrink-0"
                                />
                            </div>
                            <span class="text-sm text-slate-800">
                                Customizable transfer bonus alerts
                            </span>
                        </li>
                    </ul>
                    <button
                        class="mt-6 bg-orange text-white text-sm font-medium py-2.5 px-6 rounded shadow-sm hover:bg-orange-dark transition-colors"
                        type="button"
                        @click="$emit('checkout')"
                    >
                        Join 10xTravel+
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import Switch from '@/components/ui/switch/switch.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const { transferBonusAlertsEnabled } = useFeatureFlags();

defineProps({
    isYearlyBilling: {
        type: Boolean,
        required: true,
    },
});

defineEmits(['update:isYearlyBilling', 'checkout']);
</script>
